import { useState, useEffect, useCallback } from 'react';
import { api } from '../utils/requestInterceptor';
import { toast } from 'react-hot-toast';

/**
 * Custom hook for managing RACI assignments for a project
 * This hook provides methods to fetch, update, and manage assignments 
 * for a project and all its phases and steps in a single batch
 * 
 * @param {number|string} projectId - The ID of the project
 * @returns {Object} - Methods and state for working with RACI assignments
 */
export const useRaciAssignments = (projectId) => {
  const [allAssignments, setAllAssignments] = useState({
    project: { id: null, assignments: { R: [], A: [], C: [], I: [] } },
    phases: {},
    steps: {},
    isLoading: true,
    error: null
  });
  
  // Track phase to step relationships for roll-up functionality
  const [phaseStepMap, setPhaseStepMap] = useState({});
  
  /**
   * Fetch all assignments for the project, its phases, and steps
   */
  const fetchAllAssignments = useCallback(async () => {
    if (!projectId) return;
    
    setAllAssignments(prev => ({ ...prev, isLoading: true, error: null }));
    
    try {
      const response = await api.get(`/assignments/project/${projectId}/all`);
      
      // Create phase-step relationship map
      const phaseStepsMapping = {};
      
      // Fetch the project details to get phases
      const projectResponse = await api.get(`/projects/${projectId}`);
      if (projectResponse.data && projectResponse.data.phases) {
        for (const phase of projectResponse.data.phases) {
          const stepsResponse = await api.get(`/phases/${phase.id}/steps`);
          if (stepsResponse.data && stepsResponse.data.steps) {
            phaseStepsMapping[phase.id] = stepsResponse.data.steps.map(step => step.id);
          }
        }
      }
      
      setPhaseStepMap(phaseStepsMapping);
      
      setAllAssignments({
        project: response.data.project || { id: projectId, assignments: { R: [], A: [], C: [], I: [] } },
        phases: response.data.phases || {},
        steps: response.data.steps || {},
        isLoading: false,
        error: null
      });
    } catch (error) {
      console.error('Error fetching all assignments:', error);
      setAllAssignments(prev => ({
        ...prev,
        isLoading: false,
        error: 'Failed to load assignments'
      }));
    }
  }, [projectId]);
  
  /**
   * Get assignments for a specific entity
   * 
   * @param {string} entityType - The type of entity ('project', 'phase', or 'step')
   * @param {number|string} entityId - The ID of the entity
   * @returns {Object|null} - The RACI assignments for the entity or null if not found
   */
  const getAssignments = useCallback((entityType, entityId) => {
    if (entityType === 'project' && entityId === projectId) {
      return allAssignments.project.assignments;
    } else if (entityType === 'phase') {
      return allAssignments.phases[entityId]?.assignments;
    } else if (entityType === 'step') {
      return allAssignments.steps[entityId]?.assignments;
    }
    return null;
  }, [allAssignments, projectId]);
  
  /**
   * Get rolled-up responsible assignments for a phase by combining direct phase
   * assignments with all responsible assignments from its steps
   * 
   * @param {number|string} phaseId - The ID of the phase
   * @returns {Array} - Combined array of responsible assignments
   */
  const getRolledUpResponsibleForPhase = useCallback((phaseId) => {
    // Get direct phase assignments
    const phaseAssignments = allAssignments.phases[phaseId]?.assignments?.R || [];
    
    // Get step IDs for this phase
    const stepIds = phaseStepMap[phaseId] || [];
    
    // Create a map to avoid duplicates
    const responsibleMap = {};
    
    // Add direct phase assignments
    phaseAssignments.forEach(assignment => {
      responsibleMap[assignment.user_id] = assignment;
    });
    
    // Add rolled-up step assignments
    stepIds.forEach(stepId => {
      const stepAssignments = allAssignments.steps[stepId]?.assignments?.R || [];
      stepAssignments.forEach(assignment => {
        // If user is not already directly assigned to the phase, mark as rolled-up
        if (!responsibleMap[assignment.user_id]) {
          responsibleMap[assignment.user_id] = {
            ...assignment,
            is_rolled_up: true, // Flag to indicate this is rolled up from a step
            source_step_id: stepId // Reference to the source step
          };
        }
      });
    });
    
    // Convert back to array
    return Object.values(responsibleMap);
  }, [allAssignments, phaseStepMap]);
  
  /**
   * Get all combined assignments for a phase, with rolled-up responsible assignments
   * 
   * @param {number|string} phaseId - The ID of the phase
   * @returns {Object} - RACI assignments with rolled-up R assignments
   */
  const getPhaseAssignmentsWithRollup = useCallback((phaseId) => {
    const assignments = allAssignments.phases[phaseId]?.assignments || { R: [], A: [], C: [], I: [] };
    
    // Replace R assignments with rolled-up version
    return {
      ...assignments,
      R: getRolledUpResponsibleForPhase(phaseId)
    };
  }, [allAssignments, getRolledUpResponsibleForPhase]);
  
  /**
   * Get users assigned to a specific RACI role for an entity
   * 
   * @param {string} entityType - The type of entity
   * @param {number|string} entityId - The ID of the entity
   * @param {string} roleType - The RACI role type ('R', 'A', 'C', or 'I')
   * @returns {Array} - Array of users assigned to the role
   */
  const getRaciUsers = useCallback((entityType, entityId, roleType) => {
    // For phases with roleType 'R', use the rolled-up assignments
    if (entityType === 'phase' && roleType === 'R') {
      const rolledUpResponsible = getRolledUpResponsibleForPhase(entityId);
      return rolledUpResponsible.map(assignment => ({
        id: assignment.user_id,
        name: assignment.user_name || 'Unknown User',
        isRolledUp: assignment.is_rolled_up || false,
        sourceStepId: assignment.source_step_id
      }));
    }
    
    // For other cases, use the regular assignments
    const entityAssignments = getAssignments(entityType, entityId);
    
    if (!entityAssignments || !entityAssignments[roleType]) {
      return [];
    }
    
    return entityAssignments[roleType].map(assignment => ({
      id: assignment.user_id,
      name: assignment.user_name || 'Unknown User'
    }));
  }, [getAssignments, getRolledUpResponsibleForPhase]);
  
  /**
   * Check if a user is assigned to a specific RACI role for an entity
   * 
   * @param {number|string} userId - The ID of the user
   * @param {string} entityType - The type of entity
   * @param {number|string} entityId - The ID of the entity
   * @param {string} roleType - The RACI role type
   * @returns {boolean} - Whether the user is assigned to the role
   */
  const isUserAssigned = useCallback((userId, entityType, entityId, roleType) => {
    // Special case for phase responsible assignments
    if (entityType === 'phase' && roleType === 'R') {
      return getRolledUpResponsibleForPhase(entityId).some(a => a.user_id === userId);
    }
    
    const entityAssignments = getAssignments(entityType, entityId);
    
    if (!entityAssignments || !entityAssignments[roleType]) {
      return false;
    }
    
    return entityAssignments[roleType].some(a => a.user_id === userId);
  }, [getAssignments, getRolledUpResponsibleForPhase]);
  
  /**
   * Update an assignment (add or remove)
   * 
   * @param {string} entityType - The type of entity
   * @param {number|string} entityId - The ID of the entity
   * @param {string} roleType - The RACI role type
   * @param {number|string} userId - The ID of the user
   * @param {boolean} isAdding - Whether to add (true) or remove (false) the assignment
   * @returns {Promise<boolean>} - Whether the operation was successful
   */
  const updateAssignment = async (entityType, entityId, roleType, userId, isAdding) => {
    // Prevent adding 'R' assignments at the project level
    if (entityType === 'project' && roleType === 'R' && isAdding) {
      toast.error('Responsible assignments should be made at the phase or step level');
      return false;
    }
    
    try {
      if (isAdding) {
        // Add assignment
        const response = await api.post('/assignments', {
          entityType, entityId, userId, roleType
        });
        
        // Update local state optimistically
        if (response.data && response.data.assignment) {
          setAllAssignments(prevState => {
            const newState = { ...prevState };
            const assignment = response.data.assignment;
            
            if (entityType === 'project') {
              if (!newState.project.assignments[roleType]) {
                newState.project.assignments[roleType] = [];
              }
              newState.project.assignments[roleType].push({
                ...assignment,
                user_id: assignment.user_id,
                user_name: assignment.user_name || 'Unknown User'
              });
            } else if (entityType === 'phase') {
              if (!newState.phases[entityId]) {
                newState.phases[entityId] = { assignments: { R: [], A: [], C: [], I: [] } };
              }
              newState.phases[entityId].assignments[roleType].push({
                ...assignment,
                user_id: assignment.user_id,
                user_name: assignment.user_name || 'Unknown User'
              });
            } else if (entityType === 'step') {
              if (!newState.steps[entityId]) {
                newState.steps[entityId] = { assignments: { R: [], A: [], C: [], I: [] } };
              }
              newState.steps[entityId].assignments[roleType].push({
                ...assignment,
                user_id: assignment.user_id,
                user_name: assignment.user_name || 'Unknown User'
              });
            }
            
            return newState;
          });
        }
        
        toast.success(`User assigned as ${roleType}`);
      } else {
        // Find existing assignment ID
        const assignments = getAssignments(entityType, entityId);
        const existingAssignment = assignments?.[roleType]?.find(a => a.user_id === userId);
        
        if (existingAssignment) {
          // Remove assignment
          await api.delete(`/assignments/${existingAssignment.id}`);
          
          // Update local state optimistically
          setAllAssignments(prevState => {
            const newState = { ...prevState };
            
            if (entityType === 'project') {
              newState.project.assignments[roleType] = newState.project.assignments[roleType]
                .filter(a => a.user_id !== userId);
            } else if (entityType === 'phase') {
              newState.phases[entityId].assignments[roleType] = 
                newState.phases[entityId].assignments[roleType]
                  .filter(a => a.user_id !== userId);
            } else if (entityType === 'step') {
              newState.steps[entityId].assignments[roleType] = 
                newState.steps[entityId].assignments[roleType]
                  .filter(a => a.user_id !== userId);
            }
            
            return newState;
          });
          
          toast.success('Assignment removed');
        }
      }
      
      return true;
    } catch (error) {
      console.error('Error updating assignment:', error);
      toast.error('Failed to update assignment');
      return false;
    }
  };
  
  /**
   * Fetch assignments for multiple entities in a single batch
   * 
   * @param {string} entityType - The type of entity ('project', 'phase', or 'step')
   * @param {Array<number|string>} entityIds - Array of entity IDs
   * @returns {Promise<Object>} - Object mapping entity IDs to their assignments
   */
  const getBatchAssignments = useCallback(async (entityType, entityIds) => {
    if (!entityIds || entityIds.length === 0) {
      return {};
    }

    try {
      const response = await api.post('/assignments/batch', {
        entityType,
        entityIds
      });

      return response.data;
    } catch (error) {
      console.error('Error fetching batch assignments:', error);
      toast.error('Failed to load assignments');
      return {};
    }
  }, []);
  
  // Initialize by fetching all assignments when the component mounts or projectId changes
  useEffect(() => {
    if (projectId) {
      fetchAllAssignments();
    }
  }, [projectId, fetchAllAssignments]);
  
  return {
    // Assignment data access
    projectAssignments: allAssignments.project?.assignments,
    getPhaseAssignments: (phaseId) => allAssignments.phases[phaseId]?.assignments,
    getPhaseAssignmentsWithRollup,
    getStepAssignments: (stepId) => allAssignments.steps[stepId]?.assignments,
    getAssignments,
    getRaciUsers,
    getRolledUpResponsibleForPhase,
    
    // Assignment operations
    updateAssignment,
    isUserAssigned,
    refreshAssignments: fetchAllAssignments,
    getBatchAssignments,
    
    // Status information
    isLoading: allAssignments.isLoading,
    error: allAssignments.error
  };
};

export default useRaciAssignments; 